import { User } from './user';
import { LegFiJwtService } from '../../services/auth/legfi-jwt.service';
import moment from 'moment-timezone';
import { BillPayVendor } from './bill-pay-vendor';
import { PayableApprovalRule } from './payable-approval-rules';
import { UploadedFile } from './uploaded-file';
import Moment = moment.Moment;
import { PayableApproval } from './payable-approvals';
import { InboundEmail } from './inbound-email';
import { UnifiedBankAccount } from './unified-bank-account';
import { Transaction } from './transactions/transaction';

export interface ExpenseCategory {
    id: number;
    name?: string;
    label?: string;
    accountType: string;
}
export interface PendingTransaction {
    expenseCategory: ExpenseCategory;
    expenseCategoryAmount: number;
}

export class PayabliPayable
{
    id: number;
    amount: number;
    approvalStatus: string;
    approvalStatusAt: Moment;
    confirmedBy: number;
    confirmedByUser: User;
    confirmedAt: Moment;
    dueDate: Moment;
    invoiceDate: Moment;
    invoiceNumber: string;
    invoiceUploadedFileId: number;
    memo: string;
    ocrProcessedAt: Moment;
    organizationId: number;
    payabliBillNumber: number;
    payabliStatus: string;
    payabliStatusAt: Moment;
    paymentAmount: number;
    purchaseOrder: string;
    scheduledPaymentDate: Moment;
    terms: string;
    vendorId: number;
    createdAt: Moment;
    updatedAt: Moment;
    vendor: BillPayVendor;
    approvalRule: PayableApprovalRule;
    approvals: PayableApproval[] = [];
    approvedApprovalsCount: number;
    sentToPayabliAt: Moment;
    source: string;
    statusDescription: string;
    payabliUnifiedBankAccountId: number;
    account: UnifiedBankAccount;
    transactions: Transaction[];
    pendingTransactions: PendingTransaction[];

    attachments: UploadedFile[] = [];
    inboundEmail: InboundEmail;

    constructor(payabliPayable: any) {
        if (payabliPayable.id) {
            this.id = payabliPayable.id;
        }
        this.amount = payabliPayable.amount || 0;
        if (payabliPayable.approvalStatus) {
            this.approvalStatus = payabliPayable.approvalStatus;
        }
        if (payabliPayable.confirmedBy) {
            this.confirmedBy = payabliPayable.confirmedBy;
        }
        if (payabliPayable.invoiceNumber) {
            this.invoiceNumber = payabliPayable.invoiceNumber;
        }
        if (payabliPayable.invoiceUploadedFileId) {
            this.invoiceUploadedFileId = payabliPayable.invoiceUploadedFileId;
        }
        if (payabliPayable.memo) {
            this.memo = payabliPayable.memo;
        }
        if (payabliPayable.organizationId) {
            this.organizationId = payabliPayable.organizationId;
        }
        if (payabliPayable.payabliBillNumber) {
            this.payabliBillNumber = payabliPayable.payabliBillNumber;
        }
        if (payabliPayable.payabliStatus) {
            this.payabliStatus = payabliPayable.payabliStatus;
        }
        if (payabliPayable.paymentAmount) {
            this.paymentAmount = payabliPayable.paymentAmount;
        } else if (payabliPayable.amount) {
            // Temporarily set payment amount = amount until Payabli can support partial/over payments
            this.paymentAmount = payabliPayable.amount;
        }
        if (payabliPayable.purchaseOrder) {
            this.purchaseOrder = payabliPayable.purchaseOrder;
        }
        if (payabliPayable.terms) {
            this.terms = payabliPayable.terms;
        }
        if (payabliPayable.vendorId) {
            this.vendorId = payabliPayable.vendorId;
        }
        if (payabliPayable.statusDescription) {
            this.statusDescription = payabliPayable.statusDescription;
        }
        if (payabliPayable.pendingTransactions) {
            this.pendingTransactions = payabliPayable.pendingTransactions;
        }
        if (payabliPayable.invoiceUploadedFileId) {
            this.invoiceUploadedFileId = payabliPayable.invoiceUploadedFileId;
        }
        if (payabliPayable.payabliUnifiedBankAccountId) {
            this.payabliUnifiedBankAccountId = payabliPayable.payabliUnifiedBankAccountId;
        }

        // timestamps
        const timezone = LegFiJwtService.getTimezone();
        if (payabliPayable.approvalStatusAt) {
            this.approvalStatusAt = moment(payabliPayable.approvalStatusAt);
        }
        if (payabliPayable.createdAt) {
            this.createdAt = moment.utc(payabliPayable.createdAt, 'YYYY-MM-DD hh:mm:ss').tz(timezone);
        }
        if (payabliPayable.confirmedAt) {
            this.confirmedAt = moment.utc(payabliPayable.confirmedAt, 'YYYY-MM-DD hh:mm:ss').tz(timezone);
        }
        if (payabliPayable.updatedAt) {
            this.updatedAt = moment.utc(payabliPayable.updatedAt, 'YYYY-MM-DD hh:mm:ss').tz(timezone);
        }
        if (payabliPayable.ocrProcessedAt) {
            this.ocrProcessedAt = moment.utc(payabliPayable.ocrProcessedAt, 'YYYY-MM-DD hh:mm:ss').tz(timezone);
        }
        if (payabliPayable.dueDate) {
            this.dueDate = moment(payabliPayable.dueDate);
        }
        if (payabliPayable.invoiceDate) {
            this.invoiceDate = moment(payabliPayable.invoiceDate);
        }
        if (payabliPayable.payabliStatusAt) {
            this.payabliStatusAt = moment(payabliPayable.payabliStatusAt);
        }
        if (payabliPayable.scheduledPaymentDate) {
            this.scheduledPaymentDate = moment(payabliPayable.scheduledPaymentDate);
        }
        if (payabliPayable.sentToPayabliAt) {
            this.sentToPayabliAt = moment(payabliPayable.sentToPayabliAt);
        }
        if (payabliPayable.source) {
            this.source = payabliPayable.source;
        }

        if (payabliPayable.attachments) {
            this.attachments = payabliPayable.attachments.map((attachment) => new UploadedFile(attachment));
        } else {
            this.attachments = [];
        }

        // relationships
        if (payabliPayable.confirmedByUser) { // TODO: convert to membership
            this.confirmedByUser = new User(payabliPayable.confirmedByUser);
        }
        if (payabliPayable.vendor) {
            this.vendor = new BillPayVendor(payabliPayable.vendor);
        }
        if (payabliPayable.approvalRule) {
            this.approvalRule = new PayableApprovalRule(payabliPayable.approvalRule);
        }
        if (payabliPayable.approvals && payabliPayable.approvals.length > 0) {
            this.approvals = payabliPayable.approvals.map(approval => new PayableApproval(approval));
        }
        if (payabliPayable.approvedApprovalsCount) {
            this.approvedApprovalsCount = payabliPayable.approvedApprovalsCount;
        }
        if (payabliPayable.inboundEmail) {
            this.inboundEmail = new InboundEmail(payabliPayable.inboundEmail);
        }
        if (payabliPayable.account) {
            this.account = new UnifiedBankAccount(payabliPayable.account);
        }
        if (payabliPayable.transactions) {
            this.transactions = payabliPayable.transactions.map((transaction) => new Transaction(transaction));
        }
    }
}
